import {
  AvailabilityCalendar,
  Button,
  Card,
  StatusPill,
  CardHeader,
  Combobox,
  DocumentInput,
  FormErrorSummary,
  Modal,
  SegmentedControl,
  Switch,
} from '@hue/design-system';
import { isValidCpf } from '@hue/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { useState } from 'react';
import { getApiError } from '../lib/api.js';
import { portalApi, type PortalAppointment, type PortalPayment } from '../lib/portal-api.js';
import { civilDateOf, formatDate, formatDateTime, formatHourMinute } from '../lib/time-zone.js';
import { RequestAlert } from './data-view.js';
import { CustomerAppointmentStatus } from './customer-appointment-status.js';

export function CustomerAppointmentDetail({
  appointment,
  companyKey,
  onClose,
  embedded = false,
}: {
  appointment: PortalAppointment;
  companyKey: string;
  onClose: () => void;
  embedded?: boolean;
}) {
  const [openedAt] = useState(() => Date.now());
  const [action, setAction] = useState<'detail' | 'cancel' | 'pay' | 'reschedule'>('detail');
  const [choice, setChoice] = useState<'REFUND' | 'CREDIT' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [showConditions, setShowConditions] = useState(false);
  const [document, setDocument] = useState('');
  const [documentError, setDocumentError] = useState<string>();
  const [method, setMethod] = useState<'PIX' | 'CARD'>('PIX');
  const [charge, setCharge] = useState<PortalPayment>();
  const [date, setDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [professionalId, setProfessionalId] = useState('');
  const [reuse, setReuse] = useState(false);
  const today = civilDateOf(new Date(openedAt), appointment.timeZone);
  const maxDate = new Date(Date.parse(`${today}T12:00:00Z`) + 90 * 86400000)
    .toISOString()
    .slice(0, 10);
  const [month, setMonth] = useState(today.slice(0, 7));
  const queryClient = useQueryClient();
  const financial = useQuery({
    queryKey: ['customer', 'portal', 'financial', appointment.id],
    queryFn: () => portalApi.financial(companyKey, appointment.id),
    retry: false,
    refetchInterval: 15000,
  });
  const available = useQuery({
    queryKey: [
      'public',
      'portal',
      'reschedule',
      companyKey,
      appointment.businessUnitId,
      appointment.serviceId,
      month,
      professionalId,
    ],
    enabled: action === 'reschedule',
    queryFn: () =>
      portalApi.monthAvailability(companyKey, {
        businessUnitId: appointment.businessUnitId,
        serviceId: appointment.serviceId,
        month,
        professionalId: professionalId || undefined,
      }),
    retry: false,
  });
  const preview = useQuery({
    queryKey: ['customer', 'portal', 'cancellation', appointment.id],
    enabled: action === 'cancel',
    queryFn: () => portalApi.cancellationPreview(companyKey, appointment.id),
    retry: false,
  });
  // A escolha só existe quando sobra algo e a faixa não fixou o destino (ADR 0071).
  const choose = preview.data?.remainder === 'CHOICE' && preview.data.returnedCents > 0;
  const slots = available.data?.days.find((day) => day.date === date)?.slots ?? [];
  const money = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: appointment.currency }).format(
      cents / 100,
    );
  async function perform() {
    setError(undefined);
    if (action === 'pay' && !isValidCpf(document.replace(/\D/gu, ''))) {
      setDocumentError('Informe um CPF válido.');
      return;
    }
    if (action === 'cancel' && choose && !choice) {
      setError('Escolha receber devolução ou manter como crédito.');
      return;
    }
    if (action === 'reschedule' && (!date || !selectedSlot)) {
      setError('Selecione um dia e um horário para solicitar a remarcação.');
      return;
    }
    if (action === 'reschedule' && !reuse) {
      setError('Aceite as condições da remarcação para continuar.');
      return;
    }
    setBusy(true);
    try {
      if (action === 'cancel')
        await portalApi.cancel(
          companyKey,
          appointment.id,
          choose ? choice! : preview.data!.remainder === 'CREDIT' ? 'CREDIT' : 'REFUND',
        );
      if (action === 'reschedule')
        await portalApi.reschedule(companyKey, appointment.id, {
          date: date!,
          startTime: selectedSlot!,
          professionalId: professionalId || undefined,
          reuseDeposit: true,
        });
      if (action === 'pay') {
        await portalApi.saveBilling(companyKey, document.replace(/\D/gu, ''));
        // Com cobrança em aberto, o servidor devolve a mesma; a forma exibida passa a ser a dela.
        const created = await portalApi.pay(companyKey, appointment.id, method);
        setCharge(created);
        if (created.paymentMethod) setMethod(created.paymentMethod);
      }
      await queryClient.invalidateQueries({ queryKey: ['customer', 'portal'] });
      if (action === 'cancel') setAction('detail');
      if (action === 'reschedule') {
        setAction('detail');
        onClose();
      }
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setBusy(false);
    }
  }
  const live =
    appointment.portalOrigin &&
    ['SCHEDULED', 'CONFIRMED'].includes(appointment.status) &&
    Date.parse(appointment.startsAt) > Math.max(openedAt, financial.dataUpdatedAt) &&
    !financial.data?.cancellation;
  const body = (
    <>
      {action === 'detail' ? (
        <div className="customer-appointment-detail space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CustomerAppointmentStatus appointment={appointment} />
            <span className="text-xs text-content-muted">{appointment.companyName}</span>
          </div>
          {!embedded ? (
            <h3 className="text-xl font-semibold">{appointment.serviceDisplayName}</h3>
          ) : null}
          <dl className="grid grid-cols-2 gap-4 border-b border-surface-border pb-5 text-sm">
            <div>
              <dt className="text-xs text-content-muted">Data</dt>
              <dd className="mt-1 font-medium">
                {formatDate(appointment.startsAt, appointment.timeZone)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-content-muted">Horário</dt>
              <dd className="mt-1 font-medium">
                {formatHourMinute(appointment.startsAt, appointment.timeZone)}–
                {formatHourMinute(appointment.endsAt, appointment.timeZone)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-content-muted">Duração</dt>
              <dd className="mt-1">
                {Math.round(
                  (Date.parse(appointment.endsAt) - Date.parse(appointment.startsAt)) / 60000,
                )}{' '}
                minutos
              </dd>
            </div>
            <div>
              <dt className="text-xs text-content-muted">Fuso do atendimento</dt>
              <dd className="mt-1 break-words">{appointment.timeZone}</dd>
            </div>
          </dl>
          <div className="rounded-r-xl border-l-2 border-brand-500 bg-surface-elevated p-4 text-sm leading-6">
            {appointment.status === 'SCHEDULED'
              ? appointment.requiresConfirmation
                ? 'Aguarde a confirmação da unidade. Solicitar ou pagar não confirma o horário.'
                : 'Seu atendimento está agendado.'
              : appointment.status === 'CANCELLED'
                ? 'Agendamento cancelado.'
                : appointment.status === 'CONFIRMED'
                  ? 'Seu horário está confirmado.'
                  : appointment.status === 'COMPLETED'
                    ? 'Atendimento realizado.'
                    : 'Ausência registrada pela unidade.'}
            {appointment.queuePosition ? (
              <p className="mt-2">
                Posição na fila: {appointment.queuePosition}. A posição não garante confirmação.
              </p>
            ) : null}
          </div>
          <section className="border-b border-surface-border pb-5">
            <h3 className="mb-3 text-sm font-semibold">Pagamentos</h3>
            <dl className="grid gap-2 xl:grid-cols-3">
              <div className="rounded-xl bg-surface-elevated p-3">
                <dt className="text-xs text-content-muted">Valor do serviço</dt>
                <dd className="mt-1 font-semibold">{money(appointment.priceCents)}</dd>
              </div>
              <div className="rounded-xl bg-surface-elevated p-3">
                <dt className="text-xs text-content-muted">Adiantamento previsto</dt>
                <dd className="mt-1 font-semibold">{money(appointment.depositAmountCents)}</dd>
              </div>
              <div className="rounded-xl bg-surface-elevated p-3">
                <dt className="text-xs text-content-muted">Adiantamento confirmado</dt>
                <dd className="mt-1 font-semibold">{money(appointment.depositPaidCents)}</dd>
              </div>
            </dl>
            {appointment.depositDueAt &&
            appointment.depositAmountCents > appointment.depositPaidCents &&
            live ? (
              <p className="mt-3 text-xs text-content-muted">
                Prazo do adiantamento:{' '}
                {formatDateTime(appointment.depositDueAt, appointment.timeZone)}.
              </p>
            ) : null}
          </section>
          {financial.isPending ? (
            <p role="status" className="text-sm">
              Consultando pagamentos…
            </p>
          ) : financial.isError ? (
            <RequestAlert message="Não foi possível consultar os pagamentos. Tente novamente." />
          ) : financial.data ? (
            <>
              {financial.data.cancellation ? (
                <div className="rounded-xl border border-surface-border p-4 text-sm leading-6">
                  <StatusPill
                    className="max-w-full whitespace-normal"
                    tone={
                      financial.data.cancellation.status === 'REQUIRES_ACTION'
                        ? 'danger'
                        : financial.data.cancellation.status === 'DONE'
                          ? 'progress'
                          : 'warning'
                    }
                  >
                    {financial.data.cancellation.status === 'DONE'
                      ? 'Processamento financeiro concluído'
                      : financial.data.cancellation.status === 'REQUIRES_ACTION'
                        ? 'Devolução ou crédito pendente de regularização'
                        : 'Processamento financeiro em andamento'}
                  </StatusPill>
                  <p className="break-all">Protocolo: {financial.data.cancellation.id}</p>
                  {financial.data.cancellation.retainedCents ? (
                    <p>
                      Retido pela política de cancelamento:{' '}
                      {money(financial.data.cancellation.retainedCents)}.
                    </p>
                  ) : null}
                  {financial.data.cancellation.returnedCents === 0 ? null : (
                    <p>
                      Destino registrado
                      {financial.data.cancellation.returnedCents
                        ? ` para ${money(financial.data.cancellation.returnedCents)}`
                        : ''}
                      :{' '}
                      {financial.data.cancellation.disposition === 'REFUND'
                        ? 'receber devolução'
                        : 'manter como crédito'}
                      .
                    </p>
                  )}
                  {financial.data.cancellation.error ? (
                    <p>
                      A empresa foi avisada e precisa regularizar o processamento. Seu pedido
                      permanece registrado.
                    </p>
                  ) : null}
                  <p>O protocolo não comprova que o dinheiro já foi devolvido.</p>
                </div>
              ) : null}
              {financial.data.credit.map((credit, index) => (
                <p key={`${credit.businessUnitId}-${index}`} className="text-sm">
                  Crédito disponível deste cancelamento:{' '}
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: credit.currency,
                  }).format(credit.availableCents / 100)}
                  . {financial.data.creditScope}
                </p>
              ))}
              {financial.data.charges.map((item) => (
                <div
                  key={item.id}
                  className="space-y-2 border-t border-surface-border pt-4 text-sm"
                >
                  {item.verificationPending ? (
                    <p className="text-content-muted">
                      A consulta ao provedor está indisponível. Esta é a última situação registrada;
                      o sistema voltará a conferir.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <strong>{money(item.amountCents)}</strong>
                    <StatusPill
                      tone={
                        item.status === 'PAID'
                          ? 'positive'
                          : item.status === 'REFUNDED'
                            ? 'progress'
                            : item.status === 'WAITING'
                              ? 'warning'
                              : 'neutral'
                      }
                    >
                      {item.status === 'PAID'
                        ? 'Pagamento confirmado'
                        : item.status === 'REFUNDED'
                          ? 'Pagamento revertido'
                          : item.status === 'WAITING'
                            ? 'Aguardando pagamento'
                            : 'Consulte a situação do processamento'}
                    </StatusPill>
                  </div>
                  {item.refundStatus === 'PENDING' ? (
                    <p>Devolução solicitada; aguardando confirmação.</p>
                  ) : null}
                  {item.paymentReceiptUrl?.startsWith('https://') ? (
                    <a
                      className="block text-brand-600 underline"
                      href={item.paymentReceiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir comprovante do pagamento
                    </a>
                  ) : null}
                  {item.refundStatus === 'DONE' && item.refundReceiptUrl?.startsWith('https://') ? (
                    <a
                      className="block text-brand-600 underline"
                      href={item.refundReceiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir comprovante da devolução
                    </a>
                  ) : item.refundStatus === 'DONE' ? (
                    <p>
                      Devolução confirmada pelo provedor. Comprovante ainda não disponibilizado.
                    </p>
                  ) : null}
                </div>
              ))}
              {financial.data.policy ? (
                <section>
                  <Button
                    variant="ghost"
                    onClick={() => setShowConditions(!showConditions)}
                    aria-expanded={showConditions}
                    aria-controls="appointment-policy"
                  >
                    Ver condições aceitas
                  </Button>
                  {showConditions ? (
                    <div
                      id="appointment-policy"
                      className="mt-3 text-sm leading-6 text-content-muted"
                    >
                      <p>
                        Condições aceitas em{' '}
                        {formatDateTime(financial.data.policy.acceptedAt, appointment.timeZone)} ·
                        versão {financial.data.policy.policyVersion}.
                      </p>
                      {financial.data.policy.cancellationSummary ? (
                        <p>{financial.data.policy.cancellationSummary}</p>
                      ) : (
                        <>
                          <p>{financial.data.policy.text?.cancellation}</p>
                          {financial.data.policy.acceptedNoticeHours !== null ? (
                            <p>
                              Antecedência recomendada no pedido:{' '}
                              {financial.data.policy.acceptedNoticeHours} horas. A recomendação não
                              bloqueia cancelamento ou devolução.
                            </p>
                          ) : null}
                        </>
                      )}
                      <p>{financial.data.policy.text?.confirmation}</p>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </>
          ) : null}
          {live ? (
            <div className="flex flex-wrap justify-end gap-3 pt-2">
              {appointment.depositAmountCents > appointment.depositPaidCents ? (
                <Button onClick={() => setAction('pay')}>Pagar adiantamento</Button>
              ) : null}
              <Button variant="outline" onClick={() => setAction('reschedule')}>
                Solicitar remarcação
              </Button>
              <Button variant="outline" onClick={() => setAction('cancel')}>
                Cancelar agendamento
              </Button>
            </div>
          ) : !appointment.portalOrigin ? (
            <p className="text-sm text-content-muted">
              Entre em contato com a unidade para alterar este agendamento anterior ao portal.
            </p>
          ) : null}
        </div>
      ) : null}
      {action === 'cancel' ? (
        <div className="space-y-4">
          <p className="text-sm leading-6">
            O horário será liberado e os lembretes serão interrompidos. A devolução pode continuar
            em processamento depois do cancelamento.
          </p>
          {preview.isPending ? (
            <p className="text-sm text-content-muted" role="status">
              Calculando o valor que volta para você…
            </p>
          ) : preview.isError ? (
            <RequestAlert message={getApiError(preview.error)} />
          ) : (
            <div className="space-y-2 rounded-xl border border-surface-border p-4 text-sm leading-6">
              {preview.data.paidCents === 0 ? (
                <p>Não há adiantamento confirmado: nada é cobrado nem devolvido.</p>
              ) : (
                <>
                  <p>Adiantamento confirmado: {money(preview.data.paidCents)}.</p>
                  {preview.data.retainedCents > 0 ? (
                    <p>
                      <strong>
                        {money(preview.data.retainedCents)} fica com o estabelecimento
                      </strong>
                      {preview.data.withinHours
                        ? `, porque faltam menos de ${String(preview.data.withinHours)} ${preview.data.withinHours === 1 ? 'hora' : 'horas'} para o atendimento`
                        : ''}
                      , conforme a política aceita no pedido.
                    </p>
                  ) : null}
                  {preview.data.returnedCents > 0 ? (
                    <p>
                      Voltam para você: <strong>{money(preview.data.returnedCents)}</strong>
                      {preview.data.remainder === 'REFUND'
                        ? ', como devolução pelo meio original, sem desconto de tarifas.'
                        : preview.data.remainder === 'CREDIT'
                          ? ', como crédito nesta empresa.'
                          : '.'}
                    </p>
                  ) : null}
                </>
              )}
              {preview.data.summary ? (
                <p className="text-content-muted">{preview.data.summary}</p>
              ) : null}
            </div>
          )}
          {choose ? (
            <fieldset>
              <legend className="font-semibold text-content">
                Como você quer receber {money(preview.data!.returnedCents)}?
              </legend>
              <p className="mt-1 text-sm text-content-muted">Escolha uma das opções.</p>
              <div role="radiogroup" className="mt-3 space-y-2">
                <RemainderOption
                  checked={choice === 'REFUND'}
                  onChoose={() => setChoice('REFUND')}
                  label="Receber devolução"
                  hint={`Devolução de ${money(preview.data!.returnedCents)} pelo meio original, sem desconto de tarifas.`}
                />
                <RemainderOption
                  checked={choice === 'CREDIT'}
                  onChoose={() => setChoice('CREDIT')}
                  label="Manter como crédito"
                  hint="Somente nesta empresa. O uso em outras unidades depende da configuração da unidade onde você pagar. Valores ainda não pagos não geram crédito."
                />
              </div>
            </fieldset>
          ) : null}
        </div>
      ) : null}
      {action === 'pay' ? (
        <form
          id="portal-payment"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void perform();
          }}
          className="space-y-5"
        >
          <div className="space-y-2 text-sm leading-6">
            <p>
              Preço total do serviço: <strong>{money(appointment.priceCents)}</strong>.
            </p>
            <p>
              Adiantamento total: {money(appointment.depositAmountCents)}. Confirmado:{' '}
              {money(appointment.depositPaidCents)}.
            </p>
            <p>
              Valor a pagar agora:{' '}
              <strong>
                {money(Math.max(0, appointment.depositAmountCents - appointment.depositPaidCents))}
              </strong>
              .
            </p>
            <p>
              Restante do serviço após o adiantamento:{' '}
              {money(Math.max(0, appointment.priceCents - appointment.depositAmountCents))}.
            </p>
            {appointment.depositDueAt ? (
              <p>
                Prazo do adiantamento:{' '}
                {formatDateTime(appointment.depositDueAt, appointment.timeZone)}.
              </p>
            ) : null}
            <p>
              O pagamento não confirma o horário. Antes do atendimento você pode cancelar; o que
              fica com o estabelecimento e o que volta para você seguem a política de cancelamento
              aceita no pedido, sem desconto de tarifas na devolução.
            </p>
          </div>
          <DocumentInput
            kind="CPF"
            value={document}
            onValueChange={(value) => {
              setDocument(value);
              setDocumentError(undefined);
            }}
            error={documentError}
            hint="O CPF será usado para emitir a cobrança deste cliente nesta empresa."
          />
          {charge ? null : (
            <SegmentedControl
              label="Forma de pagamento"
              options={[
                { value: 'PIX', label: 'Pix' },
                { value: 'CARD', label: 'Cartão à vista' },
              ]}
              value={method}
              onChange={setMethod}
            />
          )}
          <FormErrorSummary />
          {charge ? (
            <div className="space-y-4 rounded-xl border border-surface-border p-4 text-sm">
              <p>
                {charge.environment === 'SIMULATED'
                  ? 'Ambiente simulado: nenhum pagamento real.'
                  : 'Cobrança gerada. Aguarde a confirmação após pagar.'}
              </p>
              {charge.pixQrImage?.startsWith('data:image/') && charge.paymentMethod === 'PIX' ? (
                <img
                  src={charge.pixQrImage}
                  alt="QR Code Pix da cobrança"
                  className="mx-auto size-48 max-w-full"
                />
              ) : null}
              {charge.pixPayload && charge.paymentMethod === 'PIX' ? (
                <p className="break-all rounded-lg bg-surface-elevated p-3">
                  Pix copia e cola: {charge.pixPayload}
                </p>
              ) : null}
              {charge.invoiceUrl?.startsWith('https://') ? (
                <a
                  href={charge.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-brand-600 underline"
                >
                  Abrir página segura de pagamento
                </a>
              ) : null}
              <p>Dados de cartão são informados no provedor de pagamento. Nunca neste portal.</p>
            </div>
          ) : null}
        </form>
      ) : null}
      {action === 'reschedule' ? (
        <div className="space-y-5">
          <p className="text-sm">
            Escolha outro horário nesta unidade. Seu pedido entra na fila do novo horário e depende
            de nova confirmação.
          </p>
          {available.data?.professionals.length ? (
            <Combobox
              label="Profissional"
              placeholder="Qualquer profissional disponível"
              selectedLabel={
                available.data.professionals.find((item) => item.id === professionalId)
                  ?.displayName ?? 'Qualquer profissional disponível'
              }
              options={[
                { value: '', label: 'Qualquer profissional disponível' },
                ...available.data.professionals.map((item) => ({
                  value: item.id,
                  label: item.displayName,
                })),
              ]}
              onSelect={(choice) => {
                if (choice.kind === 'option') {
                  setProfessionalId(choice.value);
                  setSelectedSlot(null);
                }
              }}
            />
          ) : null}
          <AvailabilityCalendar
            month={month}
            today={today}
            maxDate={maxDate}
            value={date}
            onMonthChange={(value) => {
              setMonth(value);
              setDate(null);
              setSelectedSlot(null);
            }}
            onValueChange={(value) => {
              setDate(value);
              setSelectedSlot(null);
            }}
            availability={Object.fromEntries(
              (available.data?.days ?? []).map((day) => [
                day.date,
                day.slots.length ? ('AVAILABLE' as const) : ('UNAVAILABLE' as const),
              ]),
            )}
          />
          {available.isFetching ? (
            <p role="status" className="text-sm">
              Consultando horários…
            </p>
          ) : available.isError ? (
            <RequestAlert message="Não foi possível consultar este mês." />
          ) : date ? (
            <div className="grid grid-cols-2 gap-3">
              {slots.map((slot) => (
                <button
                  key={slot.startTime}
                  type="button"
                  aria-pressed={selectedSlot === slot.startTime}
                  onClick={() => {
                    setSelectedSlot(slot.startTime);
                    setError(undefined);
                  }}
                  className={`cursor-pointer rounded-xl border p-3 text-sm ${selectedSlot === slot.startTime ? 'border-brand-500 bg-brand-500/10' : 'border-surface-border'}`}
                >
                  {slot.startTime}–{slot.endTime}
                </button>
              ))}
              {slots.length === 0 ? (
                <p className="text-sm">Sem horários neste dia. Escolha um dia com ponto dourado.</p>
              ) : null}
            </div>
          ) : null}
          <Switch
            checked={reuse}
            onChange={(value) => {
              setReuse(value);
              setError(undefined);
            }}
          >
            Concordo com a nova solicitação e com o reaproveitamento do adiantamento no novo
            horário.
          </Switch>
        </div>
      ) : null}
      {error ? <RequestAlert message={error} /> : null}
    </>
  );
  if (embedded && action === 'detail') {
    return (
      <Card>
        <CardHeader
          title={appointment.serviceDisplayName}
          icon={<CalendarDays className="size-4" />}
        />
        {body}
      </Card>
    );
  }
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          if (embedded) {
            setAction('detail');
            setError(undefined);
          } else onClose();
        }
      }}
      title={
        action === 'cancel'
          ? 'Cancelar agendamento'
          : action === 'pay'
            ? 'Pagar adiantamento'
            : action === 'reschedule'
              ? 'Solicitar outro horário'
              : 'Meu agendamento'
      }
      description={`${appointment.serviceDisplayName} · ${formatDateTime(appointment.startsAt, appointment.timeZone)}`}
      footer={
        <>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              if (action === 'detail') onClose();
              else {
                setAction('detail');
                setError(undefined);
              }
            }}
          >
            {action === 'detail' ? 'Fechar' : 'Voltar'}
          </Button>
          {action !== 'detail' && !(action === 'pay' && charge) ? (
            <Button
              type={action === 'pay' ? 'submit' : 'button'}
              form={action === 'pay' ? 'portal-payment' : undefined}
              disabled={busy || (action === 'cancel' && !preview.data)}
              onClick={action === 'pay' ? undefined : () => void perform()}
            >
              {busy
                ? 'Processando…'
                : action === 'cancel'
                  ? 'Confirmar cancelamento'
                  : action === 'reschedule'
                    ? 'Solicitar remarcação'
                    : 'Gerar pagamento'}
            </Button>
          ) : null}
        </>
      }
    >
      {body}
    </Modal>
  );
}

/// Escolha única entre devolução e crédito, com o círculo de seleção que deixa claro que só uma
/// opção vale, no mesmo padrão da sobra na conciliação.
function RemainderOption({
  checked,
  onChoose,
  label,
  hint,
}: {
  checked: boolean;
  onChoose: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onChoose}
      className={`flex w-full cursor-pointer items-start gap-3 rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
        checked
          ? 'border-brand-500 bg-brand-500/10'
          : 'border-surface-border hover:border-brand-500/40'
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border ${
          checked ? 'border-brand-500 bg-brand-500' : 'border-surface-border'
        }`}
      >
        {checked ? <span className="size-2 rounded-full bg-on-brand" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-content">{label}</span>
        <span className="mt-1 block text-sm text-content-muted">{hint}</span>
      </span>
    </button>
  );
}
